import { t } from "@lingui/macro";
import { Formik, Field } from "formik";
import { QRCodeSVG } from "qrcode.react";
import React, { useState, useEffect } from "react";
import { FaLinkedinIn } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Message, Item } from "semantic-ui-react";
import * as Yup from "yup";

import "./index.scss";
import { GetCurrentUser } from "../../../actionTypes/coreUsers";
import { ApiError } from "../../../interfaces/api/error";
import { ApiRequest } from "../../../interfaces/api/request";
import { ApiSuccess } from "../../../interfaces/api/success";
import { Violation } from "../../../interfaces/api/violation";
import { CoreUser, coreUserRoles } from "../../../interfaces/resources/coreUser";
import { editCoreUserAction } from "../../../services/api/coreUser/actions";
import { postWhatsappUser } from "../../../services/api/whatsapp/actions";
import transCoreUser from "../../../translations/constants/coreUser";
import { getUserImage } from "../../App/Image";
import { File } from "../../Form/Formik/File";
import Input from "../../Form/Formik/Input";
import { Phone } from "../../Form/Formik/Phone";
import { Select } from "../../Form/Formik/Select";
import { useApiSelector, usePrevious } from "../../Utils/hook";

interface Props {
  stateProp: "currentUser" | "coreUser";
  currentUser?: CoreUser | null;
  coreUser?: CoreUser;
}

const formName = "editCoreUser";

const SUPPORTED_FORMATS = ["jpg", "jpeg", "png"];

// Define validation schema using Yup
const validationSchema = Yup.object().shape({
  imageFile: Yup.mixed()
    .test("fileFormat", "Le fichier doit être au format jpg, jpeg ou png", (value) => {
      if (value && value.type) {
        return SUPPORTED_FORMATS.includes(value.type);
      } else {
        return true;
      }
    })
    .notRequired(),
  email: Yup.string()
    .email(t`Invalid email`)
    .required(t`Email is required`),
  phone: Yup.string(),
  linkedInUrl: Yup.string().url(t`Invalid URL`),
  roles: Yup.array().of(Yup.string()),
  calendlyLink10: Yup.string()
    .url(t`Invalid URL`)
    .nullable(),
  calendlyLink20: Yup.string()
    .url(t`Invalid URL`)
    .nullable(),
  calendlyLink30: Yup.string()
    .url(t`Invalid URL`)
    .nullable(),
});

export const Profile: React.FC<Props> = ({ stateProp, currentUser, coreUser }) => {
  const { apiErrors, apiPendingRequests, apiSuccess } = useApiSelector();
  const [success, setSuccess] = useState(false);
  const [userImage, setUserImage] = useState<any>(undefined);
  const [avatar, setAvatar] = useState<any>(undefined);
  const dispatch = useDispatch();
  const [error, setError] = useState<boolean | string>(false);
  const [errors, setErrors] = useState<Violation[]>([]);
  const prevApiSuccess = usePrevious(apiSuccess);
  const prevApiErrors = usePrevious(apiErrors);
  const whatsappCode = useSelector((state: any) => state.whatsappCode);
  // const [whatsAppStatus, setWhatsAppStatus] = useState(null);

  const user = stateProp === "currentUser" ? currentUser : coreUser;

  const submitCoreUser = (req: any, form?: string, redirectOnSuccess?: boolean | string): any => {
    dispatch(editCoreUserAction(req, form, redirectOnSuccess));
  };

  const connectWhatsapp = () => {
    dispatch(postWhatsappUser());
  };

  useEffect(() => {
    // IMPORTANT:
    // Make sure the topic here matches the one used when publishing your update.
    const topic = "account_status"; // or adjust to your actual topic

    // Use the public URL of your Mercure hub as per your configuration.
    // If you are running the hub on Docker locally with HTTP on port 3000,
    // your URL might be:
    const url = new URL(process.env.REACT_APP_MERCURE_URL || "");
    url.searchParams.append("topic", topic);

    // If your Mercure hub requires a subscriber token, you may need to append
    // a `token` parameter as well:
    url.searchParams.append("token", process.env.REACT_APP_MERCURE_SUBSCRIBER_TOKEN || "");

    // Create the EventSource
    const eventSource = new EventSource(url.toString());

    eventSource.onmessage = (event) => {
      // eslint-disable-next-line
      // Parse the JSON data and update state as needed.
      try {
        const payload = JSON.parse(event.data);
        // eslint-disable-next-line
        // For example, if the payload contains a WhatsApp connection code,
        // update your state (or dispatch an action) accordingly:
        if (payload && payload.whatsAppCode) {
          // setWhatsAppStatus(payload.whatsAppCode);
          // eslint-disable-next-line
        }
        // You can also dispatch a Redux action if you’re using Redux.
      } catch (e) {
        // eslint-disable-next-line
        console.error("Error parsing Mercure payload:", e);
      }
    };

    eventSource.onerror = (e) => {
      // eslint-disable-next-line
      console.error("Mercure EventSource error:", e);
      // Optionally, you might want to close the connection or attempt reconnects.
    };

    // Cleanup when component unmounts.
    return () => {
      eventSource.close();
    };
  }, []); // run once when component mounts

  useEffect(() => {
    if (user && avatar === undefined) {
      setUserImage(getUserImage(user.image));
    } else if (avatar) {
      setUserImage(getUserImage({ url: avatar }));
    }
  }, [user, avatar]);

  useEffect(() => {
    if (
      prevApiSuccess
      && !prevApiSuccess.some((e: ApiSuccess) => formName === e.form)
      && apiSuccess
      && apiSuccess.some((e: ApiSuccess) => formName === e.form)
    ) {
      setError(false);
      setErrors([]);
      setSuccess(true);
    }
  }, [apiSuccess, prevApiSuccess, setError, setErrors, setSuccess]);

  useEffect(() => {
    if (
      (!prevApiErrors || !prevApiErrors.some((e: ApiError) => formName === e.form))
      && apiErrors
      && apiErrors.some((e: ApiError) => formName === e.form)
    ) {
      const apiError = apiErrors.find((e: ApiError) => formName === e.form);
      if (apiError) {
        setErrors(apiError.payload.violations || []);
        setError(apiError.payload.message);
        setSuccess(false);
      }
    }
  }, [apiErrors, prevApiErrors, setError, setErrors, setSuccess]);

  const initialValues = {
    email: user?.email || "",
    phone: user?.phone || "",
    linkedInUrl: user?.linkedInUrl || "",
    roles: user?.roles || [],
    calendlyLink10: user?.calendlyLink10 || "",
    calendlyLink20: user?.calendlyLink20 || "",
    calendlyLink30: user?.calendlyLink30 || "",
    imageFile: user?.image?.url || undefined,
  };
  return (
    <div className="coreUserProfile">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          if (user && user["@id"]) {
            submitCoreUser({ ...values, imageUrl: avatar, "@id": user["@id"] }, formName);
          }

          setSubmitting(false);
        }}
        enableReinitialize={true}
      >
        {({ handleSubmit, isSubmitting, setFieldValue }): JSX.Element => (
          <div className="formContainer">
            <Form
              loading={
                apiPendingRequests.length > 0
                && apiPendingRequests.some(
                  (e: ApiRequest) => [GetCurrentUser.REQUEST].includes(e.type) && formName === e.form,
                )
              }
              success={success}
              error={!!error || errors.length > 0}
              onSubmit={handleSubmit}
            >
              <Message
                success
                header={t`PropsTalentEditionFormProps.success.header`}
                content={t`PropsTalentEditionFormProps.success.content`}
                icon="check circle"
              />
              <Message
                error
                header={t`PropsTalentEditionFormProps.error.header`}
                content={
                  <div>
                    {t`PropsTalentEditionFormProps.error.content`}: {error}
                  </div>
                }
                icon="times circle"
              />
              {user ? (
                <div className="ui center aligned header">
                  <div className="field avatarField">
                    <Item.Image centered circular size="small" {...userImage} />
                    <Field component={File} name="imageFile" className="fileInput" setImage={setAvatar} />
                  </div>

                  <div className="nameContainer">
                    {user.firstname} {user.lastname}
                  </div>
                  <div className="inputField">
                    <div className="info">
                      <Field name="email" component={Input} placeholder={t`coreUser.email`} />
                      <Field
                        name="phone"
                        component={Phone}
                        placeholder={t`coreUser.phone`}
                        onChange={(value: string) => setFieldValue("phone", value)}
                      />
                    </div>
                  </div>
                  <div className="inputField linkedinField">
                    <FaLinkedinIn className="linkedinIcon" />
                    <Field
                      name="linkedInUrl"
                      component={Input}
                      placeholder={t`coreUser.linkedInUrl`}
                      className="linkedinInput"
                    />
                  </div>
                  {currentUser && currentUser.roles.includes(coreUserRoles.admin) ? (
                    <div className="inputField">
                      <Field
                        component={Select}
                        name="roles"
                        placeholder={t`coreUser.roles`}
                        getOptionLabel={(role: string) => t({ id: transCoreUser.roles[role] })}
                        getOptionValue={(role: string) => role}
                        options={Object.keys(transCoreUser.roles)}
                        isMulti={true}
                        isSearchable={true}
                        isClearable={true}
                        label="Rôles"
                      />
                    </div>
                  ) : null}
                  <div className="inputField">
                    <Field
                      name="calendlyLink10"
                      component={Input}
                      label={t`coreUser.calendlyLink20.am`}
                      placeholder={t`coreUser.calendlyLink`}
                    />
                  </div>
                  <div className="inputField">
                    <Field
                      name="calendlyLink20"
                      component={Input}
                      label={t`coreUser.calendlyLink10.am`}
                      placeholder={t`coreUser.calendlyLink`}
                    />
                  </div>
                  {/* <div className="inputField">
                    <Field
                      name="calendlyLink30"
                      component={Input}
                      label={t`coreUser.calendlyLink30.am`}
                      placeholder={t`coreUser.calendlyLink`}
                      icon="calendar outline"
                      iconPosition="left"
                      labelPosition="right"
                    />
                  </div> */}
                </div>
              ) : null}
              <Button type="submit" content={t`CoreUserProfile.fields.submit`} disabled={isSubmitting} />
            </Form>
          </div>
        )}
      </Formik>
      <div onClick={() => connectWhatsapp()}>CONNECT WHATSAPP</div>
      {whatsappCode && <QRCodeSVG value={whatsappCode} />}
    </div>
  );
};
